// Zustand
import { create, StateCreator } from 'zustand';
import { persist, createJSONStorage, devtools } from 'zustand/middleware';

// Interface
import { Site } from 'interfaces/site';
import { Payor, PayorType } from 'interfaces';

export const ALL_SITES_VALUE = 'all-sites';
export type ProviderSiteSelection = 'single' | 'all';

interface CurrentSiteState extends Site {
  setCurrentSite: (site: Site) => void;
  clearCurrentSite: () => void;
  setDefaultSiteID: (defaultSiteID: string) => void;
  selectAllProviderSites: () => void;
  providerSiteSelection: ProviderSiteSelection;
  defaultSiteID: string;
}

export const resolveSiteDefaultPayor = (
  site: Pick<Site, 'province' | 'settings'>,
  payors: Payor[]
): Payor | null => {
  const configuredPayorID = site.settings?.defaultPayorID;
  const configuredPayor = configuredPayorID
    ? payors.find(({ id }) => id === configuredPayorID)
    : undefined;

  if (configuredPayor) {
    return configuredPayor;
  }

  return (
    payors.find(
      (payor) =>
        payor.type === PayorType.PUBLIC && payor.province === site.province
    ) ?? null
  );
};

const currentSiteStore: StateCreator<
  CurrentSiteState,
  [['zustand/devtools', never], ['zustand/persist', unknown]]
> = (set) => ({
  id: '',
  name: '',
  address: '',
  city: '',
  province: 'MB',
  fax: '',
  phone: '',
  email: '',
  postalCode: '',
  region: '',
  status: '',
  description: '',
  proFees: false,
  techFees: false,
  organizationID: '',
  hospitalCode: '',
  overhead: 0,
  invoicee: '' as any,
  invoiceRate: 0,
  siteNumber: '',
  apiID: '',
  apiSecret: '',
  apiKey: '',
  autoRemit: false,
  defaultSiteID: '',
  providerSiteSelection: 'single',
  cheque: false,
  creditCard: false,
  etransfer: false,
  creditCardPhoneNumber: null,
  etransferEmail: null,
  etransferPasswordRequirement: null,
  timezone: 'America/Winnipeg',
  allowUploads: false,
  requiresTariffIcd9: false,
  organization: undefined,
  settings: { defaultPayorID: null },

  setCurrentSite: (site) =>
    set({
      id: site.id,
      name: site.name,
      address: site.address,
      city: site.city,
      province: site.province,
      fax: site.fax,
      phone: site?.phone,
      email: site?.email,
      postalCode: site?.postalCode,
      region: site.region,
      status: site.status,
      description: site.description,
      proFees: site.proFees,
      techFees: site.techFees,
      organizationID: site.organizationID,
      hospitalCode: site.hospitalCode ?? '',
      overhead: site.overhead,
      invoicee: site.invoicee,
      invoiceRate: site.invoiceRate,
      siteNumber: site.siteNumber,
      apiID: site.apiID,
      apiSecret: site.apiSecret,
      apiKey: site.apiKey,
      autoRemit: site.autoRemit,
      cheque: site.cheque,
      creditCard: site.creditCard,
      etransfer: site.etransfer,
      creditCardPhoneNumber: site.creditCardPhoneNumber,
      etransferEmail: site.etransferEmail,
      etransferPasswordRequirement: site.etransferPasswordRequirement,
      timezone: site.timezone,
      allowUploads: site.allowUploads,
      requiresTariffIcd9: site.requiresTariffIcd9,
      organization: site.organization,
      settings: site.settings ?? { defaultPayorID: null },
      providerSiteSelection: 'single',
    }),
  clearCurrentSite: () =>
    set({
      id: '',
      name: '',
      address: '',
      city: '',
      province: 'MB',
      fax: '',
      phone: '',
      email: '',
      postalCode: '',
      region: '',
      status: '',
      description: '',
      proFees: false,
      techFees: false,
      organizationID: '',
      hospitalCode: '',
      overhead: 0,
      invoicee: '' as any,
      invoiceRate: 0,
      siteNumber: '',
      apiID: '',
      apiSecret: '',
      apiKey: '',
      autoRemit: false,
      cheque: false,
      creditCard: false,
      etransfer: false,
      creditCardPhoneNumber: null,
      etransferEmail: null,
      etransferPasswordRequirement: null,
      timezone: 'America/Winnipeg',
      allowUploads: false,
      organization: undefined,
      requiresTariffIcd9: false,
      settings: { defaultPayorID: null },
      providerSiteSelection: 'single',
    }),
  selectAllProviderSites: () => set({ providerSiteSelection: 'all' }),
  setDefaultSiteID: (defaultSiteID: string) => set({ defaultSiteID }),
});

export const useCurrentSiteStore = create<CurrentSiteState>()(
  devtools(
    persist(currentSiteStore, {
      name: 'site-storage',
      storage: createJSONStorage(() => localStorage),
    }),
    {
      name: 'CurrentSiteStore',
      anonymousActionType: 'setCurrentSite',
      storeName: 'SiteStore',
    }
  )
);
